/**
 * Fetches products from a collection using GraphQL and renders product cards
 * Replaces skeleton loaders with actual product cards
 */

async function fetchCollectionProducts(collectionHandle, cursor = null, limit = 8) {
  const afterParam = cursor ? `, after: "${cursor}"` : '';
  const graphqlQuery = {
    query: `query collectionProducts {
      collection(handle: "${collectionHandle}") {
        products(first: ${limit}${afterParam}) {
          edges {
            node {
              title
              priceRange {
                maxVariantPrice {
                  amount
                }
                minVariantPrice {
                  amount
                }
              }
              variants(first: 1) {
                edges {
                  node {
                    id
                    title
                    sku
                    barcode
                    price {
                      amount
                    }
                    compareAtPrice {
                      amount
                    }
                  }
                }
              }
              handle
              id
              tags
              media(first: 15) {
                edges {
                  cursor
                  node {
                    alt
                    mediaContentType
                    ... on MediaImage {
                      alt
                      id
                      mediaContentType
                      image {
                        altText
                        height
                        id
                        originalSrc
                        src
                        transformedSrc
                        url
                        width
                      }
                    }
                  }
                }
              }
              featuredImage {
                altText
                height
                id
                src
                width
              }
              color_variant: metafield(namespace: "custom", key: "color_variant") {
                value
              }
              select_size_guide: metafield(namespace: "custom", key: "select_size_guide") {
                value
              }
              style_number: metafield(namespace: "custom", key: "style_number") {
                value
              }
              uom_quantity: metafield(namespace: "custom", key: "uom_quantity") {
                value
              }
              parent_category: metafield(namespace: "custom", key: "parent_category") {
                value
              }
              second_subcategory: metafield(namespace: "custom", key: "second_subcategory") {
                value
              }
              child_category: metafield(namespace: "custom", key: "child_category") {
                value
              }
              bulk_box_material_code: metafield(namespace: "custom", key: "bulk_box_material_code") {
                value
              }
              sap_material_code: metafield(namespace: "custom", key: "sap_material_code") {
                value
              }
              colour_family: metafield(namespace: "custom", key: "colour_family") {
                value
              }
              jockey_collection: metafield(namespace: "custom", key: "jockey_collection") {
                value
              }
              product_color_name: metafield(namespace: "custom", key: "product_color_name") {
                value
              }
              fabric: metafield(namespace: "custom", key: "fabric") {
                value
              }
              bra_padding_filter: metafield(namespace: "custom", key: "bra_padding_filter") {
                value
              }
              pack_multipacks_Filter: metafield(namespace: "custom", key: "pack_multipacks_Filter") {
                value
              }
              closure_filter: metafield(namespace: "custom", key: "closure_filter") {
                value
              }
              waist_rise: metafield(namespace: "custom", key: "waist_rise") {
                value
              }
              bra_wiring_filter: metafield(namespace: "custom", key: "bra_wiring_filter") {
                value
              }
              back_style_filter: metafield(namespace: "custom", key: "back_style_filter") {
                value
              }
              straps_filter: metafield(namespace: "custom", key: "straps_filter") {
                value
              }
              bra_front_coverage_filter: metafield(namespace: "custom", key: "_bra_front_coverage_filter") {
                value
              }
              top_rear_coverage_filter: metafield(namespace: "custom", key: "top_rear_coverage_filter") {
                value
              }
              rear_coverage_Filter: metafield(namespace: "custom", key: "rear_coverage_Filter") {
                value
              }
              filter_colour: metafield(namespace: "custom", key: "filter_colour") {
                value
              }
              bestseller_all_applicable_filter_values: metafield(namespace: "custom", key: "bestseller_all_applicable_filter_values") {
                value
              }
              availableForSale
              vendor
              productType
              options(first: 10) {
                name
                values
              }
            }
          }
          pageInfo {
            endCursor
            hasNextPage
            hasPreviousPage
            startCursor
          }
        }
        id
        handle
      }
    }`,
    variables: {}
  };

  try {
    const response = await fetch('/api/2023-04/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
      },
      body: JSON.stringify(graphqlQuery)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      throw new Error('GraphQL query failed');
    }

    const products = data.data.collection.products.edges.map(edge => edge.node);
    const pageInfo = data.data.collection.products.pageInfo;

    return {
      products,
      pageInfo
    };
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
}

/**
 * Formats price from cents to currency string
 */
function formatPrice(amount) {
  const price = Number.parseFloat(amount);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
}

/**
 * Generates srcset from image URL
 */
function generateSrcset(imageUrl) {
  if (!imageUrl) return '';

  // Parse URL to get base URL and query parameters
  const urlParts = imageUrl.split('?');
  const baseUrl = urlParts[0];
  const existingParams = urlParts[1] || '';

  // Parse existing query parameters
  const urlParams = new URLSearchParams(existingParams);

  // Remove width parameter if it exists
  urlParams.delete('width');

  // Get base query string without width
  const baseQuery = urlParams.toString();
  const cleanUrl = baseQuery ? `${baseUrl}?${baseQuery}` : baseUrl;

  // Generate srcset with different widths
  const widths = [220, 360, 420];
  const srcsetParts = widths.map(width => {
    const separator = cleanUrl.includes('?') ? '&' : '?';
    return `${cleanUrl}${separator}width=${width} ${width}w`;
  });

  return srcsetParts.join(', ');
}

/**
 * Gets the product image URL with srcset
 * If images array has more than 4 images, uses the 4th index image (5th image)
 * Otherwise uses the featured image
 */
function getProductImage(product, index = 0) {
  const mediaEdges = product.media?.edges || [];
  let imageUrl = '';
  let imageData = null;

  // If there are more than 4 images, use the 4th index image (5th image)
  if (mediaEdges.length > 4) {
    const mediaNode = mediaEdges[4]?.node;
    if (mediaNode?.image) {
      imageUrl = mediaNode.image.transformedSrc || mediaNode.image.src || mediaNode.image.originalSrc;
      imageData = {
        src: imageUrl,
        alt: mediaNode.image.altText || mediaNode.alt || product.title,
        width: mediaNode.image.width,
        height: mediaNode.image.height
      };
    }
  }

  // Otherwise, use the featured image
  if (!imageData && product.featuredImage) {
    imageUrl = product.featuredImage.src;
    imageData = {
      src: imageUrl,
      alt: product.featuredImage.altText || product.title,
      width: product.featuredImage.width,
      height: product.featuredImage.height
    };
  }

  // Fallback to first media image if featured image is not available
  if (!imageData && mediaEdges.length > 0) {
    const mediaNode = mediaEdges[index]?.node;
    if (mediaNode?.image) {
      imageUrl = mediaNode.image.transformedSrc || mediaNode.image.src || mediaNode.image.originalSrc;
      imageData = {
        src: imageUrl,
        alt: mediaNode.image.altText || mediaNode.alt || product.title,
        width: mediaNode.image.width,
        height: mediaNode.image.height
      };
    }
  }

  if (imageData && imageUrl) {
    // Parse URL to add width=420 to src
    const urlParts = imageUrl.split('?');
    const baseUrl = urlParts[0];
    const existingParams = urlParts[1] || '';
    const urlParams = new URLSearchParams(existingParams);
    urlParams.set('width', '420');
    const srcWithWidth = `${baseUrl}?${urlParams.toString()}`;

    // Generate srcset
    const srcset = generateSrcset(imageUrl);

    return {
      ...imageData,
      src: srcWithWidth,
      srcset: srcset
    };
  }

  return null;
}

/**
 * Gets color variant count from metafield
 */
function getColorVariantCount(product) {
  try {
    const colorVariant = product.color_variant?.value;
    if (colorVariant && typeof colorVariant === 'string') {
      // If it's a JSON string, parse it
      const parsed = JSON.parse(colorVariant);
      return Array.isArray(parsed) ? parsed.length : 1;
    } else if (Array.isArray(colorVariant)) {
      return colorVariant.length;
    }
  } catch (e) {
    // If parsing fails, assume it's an array or single value
    if (Array.isArray(product.color_variant?.value)) {
      return product.color_variant.value.length;
    }
    // Log error for debugging but don't throw
    console.warn('Error parsing color variant:', e);
  }
  return 0;
}

/**
 * Formats metafield value (handles arrays by joining them)
 */
function formatMetafieldValue(metafield) {
  if (!metafield?.value) return '';
  if (Array.isArray(metafield.value)) {
    return metafield.value.join('');
  }
  return String(metafield.value);
}

/**
 * Transforms GraphQL media format to expected format
 * Converts from GraphQL format to the format expected by the frontend
 */
function transformMediaFormat(mediaNodes) {
  if (!mediaNodes || !Array.isArray(mediaNodes)) {
    return [];
  }

  return mediaNodes.map((node, index) => {
    const image = node.image;
    if (!image) return null;

    // Extract numeric ID from GID string (e.g., "gid://shopify/MediaImage/42427955282219" -> 42427955282219)
    const idMatch = node.id?.match(/\/(\d+)$/);
    const numericId = idMatch ? parseInt(idMatch[1], 10) : null;

    // Calculate aspect ratio
    const aspectRatio = image.width && image.height ? (image.width / image.height) : null;

    // Convert absolute URL to protocol-relative URL while preserving the original domain
    let src = image.src || image.transformedSrc || image.originalSrc || image.url || '';
    if (src) {
      try {
        const url = new URL(src);
        // Convert to protocol-relative URL (//domain.com/path) while keeping the original domain
        const pathAndQuery = url.pathname + (url.search || '');
        src = `//${url.hostname}${pathAndQuery}`;
      } catch (e) {
        // If URL parsing fails, try to convert https:// to // while preserving domain
        if (src.startsWith('https://')) {
          src = src.replace('https://', '//');
        } else if (src.startsWith('http://')) {
          src = src.replace('http://', '//');
        }
        // If still not protocol-relative, log warning
        if (!src.startsWith('//')) {
          console.warn('Could not transform media URL to protocol-relative format:', src);
        }
      }
    }

    // Map mediaContentType to media_type
    const mediaType = node.mediaContentType?.toLowerCase() === 'image' ? 'image' : node.mediaContentType?.toLowerCase() || 'image';

    return {
      alt: node.alt || image.altText || null,
      id: numericId,
      position: index + 1,
      preview_image: {
        aspect_ratio: aspectRatio,
        height: image.height,
        width: image.width,
        src: src
      },
      aspect_ratio: aspectRatio,
      height: image.height,
      media_type: mediaType,
      src: src,
      width: image.width
    };
  }).filter(item => item !== null);
}

/**
 * Generates HTML for a product card
 */
function generateProductCardHTML(product, index) {
  const productId = product.id.replace('gid://shopify/Product/', '');
  const colorVariantsIds = JSON.parse(product.color_variant?.value || '[]').map(item => item.split('/').pop()).join(',');
  const productHandle = product.handle;
  const productUrl = `/products/${productHandle}`;
  const productTitle = product.title;

  // Format metafield values
  const jockeyCollection = formatMetafieldValue(product.jockey_collection);
  const fabric = formatMetafieldValue(product.fabric);
  const braPaddingFilter = formatMetafieldValue(product.bra_padding_filter);
  const packMultipacksFilter = formatMetafieldValue(product.pack_multipacks_Filter);
  const closureFilter = formatMetafieldValue(product.closure_filter);
  const waistRise = formatMetafieldValue(product.waist_rise);
  const braWiringFilter = formatMetafieldValue(product.bra_wiring_filter);
  const backStyleFilter = formatMetafieldValue(product.back_style_filter);
  const strapsFilter = formatMetafieldValue(product.straps_filter);
  const braFrontCoverageFilter = formatMetafieldValue(product.bra_front_coverage_filter);
  const topRearCoverageFilter = formatMetafieldValue(product.top_rear_coverage_filter);
  const rearCoverageFilter = formatMetafieldValue(product.rear_coverage_Filter);
  const filterColour = formatMetafieldValue(product.filter_colour);
  const applicableFilters = formatMetafieldValue(product.bestseller_all_applicable_filter_values);
  const styleNumber = product.style_number?.value || '';

  // Format options (join all option values with commas)
  const options = product.options?.map(option => option.values.join(',')).join(',') || '';

  // Format max price (convert from cents to rupees, remove decimals)
  const maxPrice = product.priceRange?.maxVariantPrice?.amount || '0';
  const maxPriceFormatted = Number.parseFloat(maxPrice).toFixed(0);

  // Get product image
  const primaryImage = getProductImage(product, 0);
  const imageSrc = primaryImage?.src || '';
  const imageAlt = primaryImage?.alt || productTitle;

  // Price information
  const minPrice = product.priceRange?.minVariantPrice?.amount || '0';

  // Format price for display (remove currency symbol from formatPrice and add ₹)
  const formattedMinPrice = `₹${Number.parseFloat(minPrice).toFixed(0)}`

  // Color variant count
  const colorVariantCount = getColorVariantCount(product);
  const isJkyStudio = (product.tags || []).includes('JKY Studio');
  const colorVariantBadge = colorVariantCount > 0 && !isJkyStudio
    ? `<div class="card-five-svg" onclick="ATBModalOpen(event, '${productId}', '${colorVariantsIds}', '${product.select_size_guide?.value || ''}', 'category-page')">
        <div class="card-five-svg-inner">
          <p class="card-five-number">${colorVariantCount}</p>
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="37" viewBox="0 0 48 37" fill="none">
            <rect x="28.98" y="5.774" width="18.281" height="25.313" rx="3.516" fill="#C46B4F" stroke="#fff" stroke-width="1.406"/><rect x=".855" y="5.774" width="16.875" height="25.313" rx="3.516" fill="#557CA6" stroke="#fff" stroke-width="1.406"/><rect x="10.699" y="1.556" width="26.719" height="33.75" rx="6.328" fill="#464F51" stroke="#fff" stroke-width="1.406"/>
          </svg>
        </div>
        <svg class="loading-spinner" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><style>.spinner_aj0A{transform-origin:center;animation:spinner_KYSC .75s infinite linear}@keyframes spinner_KYSC{100%{transform:rotate(360deg)}}</style><path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A"></path></svg>
      </div>`
    : '';

  // Get first variant for price comparison
  const firstVariant = product.variants?.edges?.[0]?.node;
  const variantCompareAtPrice = firstVariant?.compareAtPrice?.amount || '0';
  const variantPrice = firstVariant?.price?.amount || '0';
  const variantTitle = firstVariant?.title || '';
  const variantSku = firstVariant?.sku || '';
  const variantBarcode = firstVariant?.barcode || '';

  // Tags
  const tags = product.tags || [];
  const isBestseller = tags.includes('Bestseller');
  const isNewStyle = tags.some(tag => ['New Style', 'new style', 'NEW STYLE'].includes(tag));
  const hasOneDayDelivery = tags.includes('1 day delivery');
  const hasMultipackCombo = tags.includes('multipack_combo');
  const bestsellerTag = isBestseller
    ? `<div class="bestsellerTag">
        <svg width="32" height="71" viewBox="0 0 32 71" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M29.7966 0.781671C30.3731 0.748469 31.073 2.68026 31.5712 5.36966H28.5586C28.5616 4.79616 28.612 3.93667 28.8604 2.92927C29.0816 2.03205 29.3827 0.805819 29.7966 0.781671Z" fill="#232323"></path>
          <path d="M2.34883 0.781671C1.77235 0.748469 1.07243 2.68101 0.574219 5.37041H3.58684C3.58383 4.79691 3.53341 3.93742 3.28505 2.93003C3.06379 2.03205 2.76351 0.805819 2.34883 0.781671Z" fill="#232323"></path>
          <path d="M29.5742 0.394418C29.526 0.391573 29.4764 0.390625 29.4252 0.390625C29.3763 0.390625 29.3281 0.391573 29.2822 0.393469H2.86625C2.8203 0.391573 2.77286 0.390625 2.7232 0.390625C2.67206 0.390625 2.6224 0.392521 2.57422 0.394418C2.77434 0.892196 2.91369 1.31223 3.0019 1.59382C3.64082 3.63425 3.77868 5.37885 3.77646 6.21132C3.77572 6.56877 3.77498 6.92622 3.7735 7.28272H3.77646V70.3906C5.16771 68.6109 16.0738 56.3732 16.0738 56.3732C16.0738 56.3732 26.98 68.6109 28.3712 70.3906V7.28368H28.3742C28.3734 6.92623 28.3727 6.56972 28.3712 6.21227C28.369 5.37979 28.5069 3.6352 29.1458 1.59477C29.234 1.31317 29.3733 0.893144 29.5735 0.395366L29.5742 0.394418Z" fill="#3D3B3B"></path>
          <path d="M16.7728 9.86062L17.2428 9.86062L17.2428 10.9906C17.2428 11.224 17.2828 11.434 17.3628 11.6206C17.4428 11.8073 17.5594 11.954 17.7128 12.0606C17.8661 12.1673 18.0528 12.2206 18.2728 12.2206C18.6061 12.2206 18.8628 12.104 19.0428 11.8706C19.2294 11.6373 19.3228 11.344 19.3228 10.9906L19.3228 10.0406L13.8228 10.0406L13.8228 11.0906C13.8228 11.3906 13.8761 11.654 13.9828 11.8806C14.0894 12.114 14.2428 12.294 14.4428 12.4206C14.6428 12.554 14.8861 12.6206 15.1728 12.6206C15.3861 12.6206 15.5761 12.584 15.7428 12.5106C15.9094 12.4373 16.0494 12.3306 16.1628 12.1906C16.2761 12.0573 16.3594 11.8973 16.4128 11.7106C16.4728 11.524 16.5028 11.3173 16.5028 11.0906L16.5028 9.86062L16.9728 9.86062L16.9728 11.0906C16.9728 11.4306 16.9361 11.744 16.8628 12.0306C16.7894 12.3173 16.6761 12.5673 16.5228 12.7806C16.3761 13.0006 16.1861 13.1706 15.9528 13.2906C15.7194 13.4106 15.4428 13.4706 15.1228 13.4706C14.6628 13.4706 14.2794 13.364 13.9728 13.1506C13.6728 12.944 13.4461 12.6606 13.2928 12.3006C13.1461 11.9473 13.0728 11.544 13.0728 11.0906L13.0728 9.19062L20.0728 9.19063L20.0728 10.9906C20.0728 11.4106 20.0094 11.774 19.8828 12.0806C19.7561 12.394 19.5628 12.6373 19.3028 12.8106C19.0494 12.984 18.7228 13.0706 18.3228 13.0706C17.9961 13.0706 17.7161 12.984 17.4828 12.8106C17.2494 12.6373 17.0728 12.394 16.9528 12.0806C16.8328 11.774 16.7728 11.4106 16.7728 10.9906L16.7728 9.86062ZM12.9728 16.4968C12.9728 16.0701 13.0728 15.6901 13.2728 15.3568C13.4794 15.0301 13.7628 14.7735 14.1228 14.5868C14.4828 14.4068 14.8994 14.3168 15.3728 14.3168C15.8528 14.3168 16.2728 14.4101 16.6328 14.5968C16.9928 14.7901 17.2728 15.0568 17.4728 15.3968C17.6728 15.7435 17.7728 16.1435 17.7728 16.5968C17.7728 17.0501 17.6761 17.4368 17.4828 17.7568C17.2961 18.0835 17.0261 18.3335 16.6728 18.5068C16.3194 18.6801 15.8994 18.7668 15.4128 18.7668C15.3661 18.7668 15.3161 18.7635 15.2628 18.7568C15.2161 18.7568 15.1861 18.7568 15.1728 18.7568L15.1728 14.8268L15.8528 14.8268L15.8528 18.1268L15.3928 17.8068C15.4261 17.8268 15.4761 17.8468 15.5428 17.8668C15.6161 17.8868 15.6761 17.8968 15.7228 17.8968C15.9961 17.8968 16.2328 17.8401 16.4328 17.7268C16.6328 17.6201 16.7894 17.4701 16.9028 17.2768C17.0161 17.0835 17.0728 16.8568 17.0728 16.5968C17.0728 16.2901 17.0061 16.0268 16.8728 15.8068C16.7461 15.5935 16.5594 15.4268 16.3128 15.3068C16.0728 15.1935 15.7761 15.1335 15.4228 15.1268C15.0628 15.1268 14.7561 15.1835 14.5028 15.2968C14.2494 15.4101 14.0561 15.5768 13.9228 15.7968C13.7894 16.0168 13.7228 16.2835 13.7228 16.5968C13.7228 16.9101 13.7894 17.1868 13.9228 17.4268C14.0628 17.6668 14.2728 17.8701 14.5528 18.0368L14.1428 18.6868C13.7561 18.4468 13.4628 18.1435 13.2628 17.7768C13.0694 17.4168 12.9728 16.9901 12.9728 16.4968ZM14.4828 20.0533C14.3428 20.1466 14.2128 20.2533 14.0928 20.3733C13.9728 20.4933 13.8761 20.6299 13.8028 20.7833C13.7361 20.9433 13.7028 21.1166 13.7028 21.3033C13.7028 21.5299 13.7528 21.7066 13.8528 21.8333C13.9594 21.9666 14.1028 22.0333 14.2828 22.0333C14.4428 22.0333 14.5761 21.9799 14.6828 21.8733C14.7961 21.7733 14.8894 21.6433 14.9628 21.4833C15.0428 21.3233 15.1161 21.1533 15.1828 20.9733C15.2628 20.7733 15.3561 20.5699 15.4628 20.3633C15.5761 20.1633 15.7194 19.9933 15.8928 19.8533C16.0728 19.7199 16.2961 19.6533 16.5628 19.6533C16.8361 19.6533 17.0628 19.7233 17.2428 19.8633C17.4228 20.0099 17.5561 20.1966 17.6428 20.4233C17.7294 20.6566 17.7728 20.8966 17.7728 21.1433C17.7728 21.3899 17.7328 21.6199 17.6528 21.8333C17.5728 22.0533 17.4661 22.2466 17.3328 22.4133C17.1994 22.5799 17.0461 22.7099 16.8728 22.8033L16.4628 22.1633C16.6361 22.0366 16.7794 21.8766 16.8928 21.6833C17.0061 21.4966 17.0628 21.2833 17.0628 21.0433C17.0628 20.8766 17.0261 20.7333 16.9528 20.6133C16.8794 20.4933 16.7661 20.4333 16.6128 20.4333C16.4928 20.4333 16.3861 20.4799 16.2928 20.5733C16.1994 20.6666 16.1161 20.7866 16.0428 20.9333C15.9694 21.0799 15.8994 21.2333 15.8328 21.3933C15.7261 21.6599 15.6094 21.9033 15.4828 22.1233C15.3628 22.3433 15.2161 22.5166 15.0428 22.6433C14.8694 22.7766 14.6461 22.8433 14.3728 22.8433C13.9728 22.8433 13.6394 22.6966 13.3728 22.4033C13.1061 22.1166 12.9728 21.7366 12.9728 21.2633C12.9728 20.9566 13.0261 20.6799 13.1328 20.4333C13.2461 20.1866 13.3894 19.9766 13.5628 19.8033C13.7361 19.6366 13.9128 19.5099 14.0928 19.4233L14.4828 20.0533ZM17.6728 23.1965L17.6728 25.4965L16.9228 25.4965L16.9228 23.1965L17.6728 23.1965ZM19.2728 23.9465L19.2728 24.7465L13.0728 24.7465L13.0728 23.9465L19.2728 23.9465ZM14.4828 26.4888C14.3428 26.5822 14.2128 26.6888 14.0928 26.8088C13.9728 26.9288 13.8761 27.0655 13.8028 27.2188C13.7361 27.3788 13.7028 27.5522 13.7028 27.7388C13.7028 27.9655 13.7528 28.1422 13.8528 28.2688C13.9594 28.4022 14.1028 28.4688 14.2828 28.4688C14.4428 28.4688 14.5761 28.4155 14.6828 28.3088C14.7961 28.2088 14.8894 28.0788 14.9628 27.9188C15.0428 27.7588 15.1161 27.5888 15.1828 27.4088C15.2628 27.2088 15.3561 27.0055 15.4628 26.7988C15.5761 26.5988 15.7194 26.4288 15.8928 26.2888C16.0728 26.1555 16.2961 26.0888 16.5628 26.0888C16.8361 26.0888 17.0628 26.1588 17.2428 26.2988C17.4228 26.4455 17.5561 26.6322 17.6428 26.8588C17.7294 27.0922 17.7728 27.3322 17.7728 27.5788C17.7728 27.8255 17.7328 28.0555 17.6528 28.2688C17.5728 28.4888 17.4661 28.6822 17.3328 28.8488C17.1994 29.0155 17.0461 29.1455 16.8728 29.2388L16.4628 28.5988C16.6361 28.4722 16.7794 28.3122 16.8928 28.1188C17.0061 27.9322 17.0628 27.7188 17.0628 27.4788C17.0628 27.3122 17.0261 27.1688 16.9528 27.0488C16.8794 26.9288 16.7661 26.8688 16.6128 26.8688C16.4928 26.8688 16.3861 26.9155 16.2928 27.0088C16.1994 27.1022 16.1161 27.2222 16.0428 27.3688C15.9694 27.5155 15.8994 27.6688 15.8328 27.8288C15.7261 28.0955 15.6094 28.3388 15.4828 28.5588C15.3628 28.7788 15.2161 28.9522 15.0428 29.0788C14.8694 29.2122 14.6461 29.2788 14.3728 29.2788C13.9728 29.2788 13.6394 29.1322 13.3728 28.8388C13.1061 28.5522 12.9728 28.1722 12.9728 27.6988C12.9728 27.3922 13.0261 27.1155 13.1328 26.8688C13.2461 26.6222 13.3894 26.4122 13.5628 26.2388C13.7361 26.0722 13.9128 25.9455 14.0928 25.8588L14.4828 26.4888ZM12.9728 32.112C12.9728 31.6854 13.0728 31.3054 13.2728 30.972C13.4794 30.6454 13.7628 30.3887 14.1228 30.202C14.4828 30.022 14.8994 29.932 15.3728 29.932C15.8528 29.932 16.2728 30.0254 16.6328 30.212C16.9928 30.4054 17.2728 30.672 17.4728 31.012C17.6728 31.3587 17.7728 31.7587 17.7728 32.212C17.7728 32.6654 17.6761 33.052 17.4828 33.372C17.2961 33.6987 17.0261 33.9487 16.6728 34.122C16.3194 34.2954 15.8994 34.382 15.4128 34.382C15.3661 34.382 15.3161 34.3787 15.2628 34.372C15.2161 34.372 15.1861 34.372 15.1728 34.372L15.1728 30.442L15.8528 30.442L15.8528 33.742L15.3928 33.422C15.4261 33.442 15.4761 33.462 15.5428 33.482C15.6161 33.502 15.6761 33.512 15.7228 33.512C15.9961 33.512 16.2328 33.4554 16.4328 33.342C16.6328 33.2354 16.7894 33.0854 16.9028 32.892C17.0161 32.6987 17.0728 32.472 17.0728 32.212C17.0728 31.9054 17.0061 31.642 16.8728 31.422C16.7461 31.2087 16.5594 31.042 16.3128 30.922C16.0728 30.8087 15.7761 30.7487 15.4228 30.742C15.0628 30.742 14.7561 30.7987 14.5028 30.912C14.2494 31.0254 14.0561 31.192 13.9228 31.412C13.7894 31.632 13.7228 31.8987 13.7228 32.212C13.7228 32.5254 13.7894 32.802 13.9228 33.042C14.0628 33.282 14.2728 33.4854 14.5528 33.652L14.1428 34.302C13.7561 34.062 13.4628 33.7587 13.2628 33.392C13.0694 33.032 12.9728 32.6054 12.9728 32.112ZM20.8728 35.4785L20.8728 36.2785L13.0728 36.2785L13.0728 35.4785L20.8728 35.4785ZM20.8728 37.7832L20.8728 38.5832L13.0728 38.5832L13.0728 37.7832L20.8728 37.7832ZM12.9728 41.8679C12.9728 41.4412 13.0728 41.0612 13.2728 40.7279C13.4794 40.4012 13.7628 40.1446 14.1228 39.9579C14.4828 39.7779 14.8994 39.6879 15.3728 39.6879C15.8528 39.6879 16.2728 39.7812 16.6328 39.9679C16.9928 40.1612 17.2728 40.4279 17.4728 40.7679C17.6728 41.1146 17.7728 41.5146 17.7728 41.9679C17.7728 42.4212 17.6761 42.8079 17.4828 43.1279C17.2961 43.4546 17.0261 43.7046 16.6728 43.8779C16.3194 44.0512 15.8994 44.1379 15.4128 44.1379C15.3661 44.1379 15.3161 44.1346 15.2628 44.1279C15.2161 44.1279 15.1861 44.1279 15.1728 44.1279L15.1728 40.1979L15.8528 40.1979L15.8528 43.4979L15.3928 43.1779C15.4261 43.1979 15.4761 43.2179 15.5428 43.2379C15.6161 43.2579 15.6761 43.2679 15.7228 43.2679C15.9961 43.2679 16.2328 43.2112 16.4328 43.0979C16.6328 42.9912 16.7894 42.8412 16.9028 42.6479C17.0161 42.4546 17.0728 42.2279 17.0728 41.9679C17.0728 41.6612 17.0061 41.3979 16.8728 41.1779C16.7461 40.9646 16.5594 40.7979 16.3128 40.6779C16.0728 40.5646 15.7761 40.5046 15.4228 40.4979C15.0628 40.4979 14.7561 40.5546 14.5028 40.6679C14.2494 40.7812 14.0561 40.9479 13.9228 41.1679C13.7894 41.3879 13.7228 41.6546 13.7228 41.9679C13.7228 42.2812 13.7894 42.5579 13.9228 42.7979C14.0628 43.0379 14.2728 43.2412 14.5528 43.4079L14.1428 44.0579C13.7561 43.8179 13.4628 43.5146 13.2628 43.1479C13.0694 42.7879 12.9728 42.3612 12.9728 41.8679ZM17.6728 46.0344L13.0728 46.0344L13.0728 45.2344L17.6728 45.2344L17.6728 46.0344ZM16.7928 47.4244C16.8728 47.3177 16.9294 47.221 16.9628 47.1344C17.0028 47.0477 17.0228 46.9377 17.0228 46.8044C17.0228 46.6044 16.9728 46.4477 16.8728 46.3344C16.7728 46.221 16.6361 46.141 16.4628 46.0944C16.2894 46.0544 16.0928 46.0344 15.8728 46.0344L15.8728 45.6744C16.2394 45.6744 16.5628 45.7377 16.8428 45.8644C17.1294 45.9977 17.3561 46.1677 17.5228 46.3744C17.6894 46.581 17.7728 46.791 17.7728 47.0044C17.7728 47.171 17.7494 47.3244 17.7028 47.4644C17.6628 47.611 17.5794 47.7444 17.4528 47.8644L16.7928 47.4244Z" fill="white"></path>
        </svg>
      </div>`
    : (isNewStyle ? `<div class="bestsellerTag new-plp-tag">
        <svg xmlns="http://www.w3.org/2000/svg" width="27" height="70" viewBox="0 0 27 70" fill="none">
          <path d="M26.9993 0.00665138C26.9996 0.00606097 26.9992 0.00533211 26.9986 0.00527649C26.9508 0.00132239 26.9017 0 26.851 0C26.8021 0 26.7539 0.00134889 26.708 0.00404667H0.292036C0.246081 0.00134889 0.198644 0 0.148983 0C0.0724897 0 0.0197703 0.0753204 0.04019 0.149038C0.218937 0.794334 0.345503 1.3385 0.427677 1.71174C1.0666 4.61455 1.20446 7.0965 1.20224 8.28083C1.2015 8.78886 1.20076 9.2969 1.19928 9.80359C1.19928 9.80441 1.19994 9.80507 1.20076 9.80507C1.20158 9.80507 1.20224 9.80574 1.20224 9.80655V57.2522C1.20224 64.2926 13.95 70 13.95 70C13.95 70 25.797 64.6959 25.797 58.153V9.8079C25.797 9.80708 25.7977 9.80642 25.7985 9.80642C25.7993 9.80642 25.8 9.80576 25.8 9.80494C25.7992 9.2969 25.7985 8.79021 25.797 8.28218C25.7948 7.09785 25.9327 4.6159 26.5716 1.71309C26.6598 1.31249 26.7991 0.714969 26.9992 0.00684714C26.9992 0.00677923 26.9993 0.00671322 26.9993 0.00665138Z" fill="url(#paint0_linear_10167887528235)"></path>
          <path d="M18.2734 14.1L18.2734 14.95L10.9234 14.95L16.4834 9.65L11.2734 9.65L11.2734 8.8L18.6234 8.8L13.0634 14.1L18.2734 14.1ZM11.1734 18.2839C11.1734 17.8572 11.2734 17.4772 11.4734 17.1439C11.6801 16.8172 11.9634 16.5606 12.3234 16.3739C12.6834 16.1939 13.1001 16.1039 13.5734 16.1039C14.0534 16.1039 14.4734 16.1972 14.8334 16.3839C15.1934 16.5772 15.4734 16.8439 15.6734 17.1839C15.8734 17.5306 15.9734 17.9306 15.9734 18.3839C15.9734 18.8372 15.8768 19.2239 15.6834 19.5439C15.4968 19.8706 15.2268 20.1206 14.8734 20.2939C14.5201 20.4672 14.1001 20.5539 13.6134 20.5539C13.5668 20.5539 13.5168 20.5506 13.4634 20.5439C13.4168 20.5439 13.3868 20.5439 13.3734 20.5439L13.3734 16.6139L14.0534 16.6139L14.0534 19.9139L13.5934 19.5939C13.6268 19.6139 13.6768 19.6339 13.7434 19.6539C13.8168 19.6739 13.8768 19.6839 13.9234 19.6839C14.1968 19.6839 14.4334 19.6272 14.6334 19.5139C14.8334 19.4072 14.9901 19.2572 15.1034 19.0639C15.2168 18.8706 15.2734 18.6439 15.2734 18.3839C15.2734 18.0772 15.2068 17.8139 15.0734 17.5939C14.9468 17.3806 14.7601 17.2139 14.5134 17.0939C14.2734 16.9806 13.9768 16.9206 13.6234 16.9139C13.2634 16.9139 12.9568 16.9706 12.7034 17.0839C12.4501 17.1972 12.2568 17.3639 12.1234 17.5839C11.9901 17.8039 11.9234 18.0706 11.9234 18.3839C11.9234 18.6972 11.9901 18.9739 12.1234 19.2139C12.2634 19.4539 12.4734 19.6572 12.7534 19.8239L12.3434 20.4739C11.9568 20.2339 11.6634 19.9306 11.4634 19.5639C11.2701 19.2039 11.1734 18.7772 11.1734 18.2839ZM15.8734 20.8039L15.8734 21.7039L12.8834 22.8539L16.2234 24.1039L12.8634 25.4439L15.8734 26.6039L15.8734 27.5039L10.9234 25.5039L14.2734 24.1139L10.9234 22.8039L15.8734 20.8039ZM13.3734 32.0188L14.1734 32.3188L14.1734 35.4188L13.3734 35.7188L13.3734 32.0188ZM16.6934 33.8488L13.9134 32.6688L13.7334 32.5888L11.2734 31.5488L11.2734 30.5988L18.6234 33.8488L11.2734 37.0988L11.2734 36.1488L13.6734 35.1288L13.8734 35.0488L16.6934 33.8488ZM15.8734 38.7004L11.2734 38.7004L11.2734 37.9004L15.8734 37.9004L15.8734 38.7004ZM14.9934 40.0904C15.0734 39.9837 15.1301 39.8871 15.1634 39.8004C15.2034 39.7137 15.2234 39.6037 15.2234 39.4704C15.2234 39.2704 15.1734 39.1137 15.0734 39.0004C14.9734 38.8871 14.8368 38.8071 14.6634 38.7604C14.4901 38.7204 14.2934 38.7004 14.0734 38.7004L14.0734 38.3404C14.4401 38.3404 14.7634 38.4037 15.0434 38.5304C15.3301 38.6637 15.5568 38.8337 15.7234 39.0404C15.8901 39.2471 15.9734 39.4571 15.9734 39.6704C15.9734 39.8371 15.9501 39.9904 15.9034 40.1304C15.8634 40.2771 15.7801 40.4104 15.6534 40.5304L14.9934 40.0904ZM15.8734 42.1281L11.2734 42.1281L11.2734 41.3281L15.8734 41.3281L15.8734 42.1281ZM14.9934 43.5181C15.0734 43.4115 15.1301 43.3148 15.1634 43.2281C15.2034 43.1415 15.2234 43.0315 15.2234 42.8981C15.2234 42.6981 15.1734 42.5415 15.0734 42.4281C14.9734 42.3148 14.8368 42.2348 14.6634 42.1881C14.4901 42.1481 14.2934 42.1281 14.0734 42.1281L14.0734 41.7681C14.4401 41.7681 14.7634 41.8315 15.0434 41.9581C15.3301 42.0915 15.5568 42.2615 15.7234 42.4681C15.8901 42.6748 15.9734 42.8848 15.9734 43.0981C15.9734 43.2648 15.9501 43.4181 15.9034 43.5581C15.8634 43.7048 15.7801 43.8381 15.6534 43.9581L14.9934 43.5181ZM17.7734 44.7559C17.9268 44.7559 18.0568 44.8092 18.1634 44.9159C18.2701 45.0292 18.3234 45.1592 18.3234 45.3059C18.3234 45.4592 18.2701 45.5892 18.1634 45.6959C18.0568 45.8025 17.9268 45.8559 17.7734 45.8559C17.6268 45.8559 17.4968 45.8025 17.3834 45.6959C17.2768 45.5892 17.2234 45.4592 17.2234 45.3059C17.2234 45.1592 17.2768 45.0292 17.3834 44.9159C17.4968 44.8092 17.6268 44.7559 17.7734 44.7559ZM15.8734 44.9059L15.8734 45.7059L11.2734 45.7059L11.2734 44.9059L15.8734 44.9059ZM15.8734 46.6035L15.8734 47.5035L12.6234 48.8035L15.8734 50.1035L15.8734 51.0035L10.9234 48.8035L15.8734 46.6035ZM12.6934 52.1178C12.5201 52.1178 12.3701 52.1578 12.2434 52.2378C12.1168 52.3245 12.0201 52.4411 11.9534 52.5878C11.8934 52.7411 11.8634 52.9145 11.8634 53.1078C11.8634 53.3545 11.9134 53.5745 12.0134 53.7678C12.1134 53.9678 12.2534 54.1245 12.4334 54.2378C12.6201 54.3578 12.8334 54.4178 13.0734 54.4178L12.4734 54.5778C12.1801 54.5778 11.9368 54.4878 11.7434 54.3078C11.5501 54.1345 11.4068 53.9178 11.3134 53.6578C11.2201 53.4045 11.1734 53.1511 11.1734 52.8978C11.1734 52.6178 11.2301 52.3545 11.3434 52.1078C11.4634 51.8678 11.6334 51.6745 11.8534 51.5278C12.0734 51.3811 12.3401 51.3078 12.6534 51.3078C13.1001 51.3078 13.4568 51.4645 13.7234 51.7778C13.9968 52.0978 14.1334 52.5411 14.1334 53.1078C14.1334 53.4411 14.0934 53.7178 14.0134 53.9378C13.9401 54.1645 13.8534 54.3445 13.7534 54.4778C13.6601 54.6111 13.5834 54.7011 13.5234 54.7478L13.0534 54.7478C13.2134 54.5145 13.3268 54.2745 13.3934 54.0278C13.4668 53.7811 13.5034 53.5178 13.5034 53.2378C13.5034 52.9778 13.4701 52.7645 13.4034 52.5978C13.3368 52.4378 13.2434 52.3178 13.1234 52.2378C13.0034 52.1578 12.8601 52.1178 12.6934 52.1178ZM14.8134 51.9978L15.4034 51.6078C15.5301 51.7811 15.6568 52.0178 15.7834 52.3178C15.9101 52.6245 15.9734 52.9911 15.9734 53.4178C15.9734 53.7778 15.9168 54.0911 15.8034 54.3578C15.6968 54.6311 15.5401 54.8411 15.3334 54.9878C15.1268 55.1411 14.8734 55.2178 14.5734 55.2178L11.2734 55.2178L11.2734 54.4178L14.4634 54.4178C14.6234 54.4178 14.7534 54.3911 14.8534 54.3378C14.9601 54.2845 15.0434 54.2078 15.1034 54.1078C15.1634 54.0145 15.2068 53.9045 15.2334 53.7778C15.2601 53.6511 15.2734 53.5178 15.2734 53.3778C15.2734 53.1511 15.2468 52.9445 15.1934 52.7578C15.1401 52.5778 15.0768 52.4245 15.0034 52.2978C14.9301 52.1711 14.8668 52.0711 14.8134 51.9978ZM19.0734 56.5723L19.0734 57.3723L11.2734 57.3723L11.2734 56.5723L19.0734 56.5723Z" fill="white"></path>
          <defs>
            <linearGradient id="paint0_linear_10167887528235" x1="13.4885" y1="4.11276" x2="11.785" y2="181.902" gradientUnits="userSpaceOnUse">
              <stop offset="0.14" stop-color="#3D3B3B"></stop>
              <stop offset="0.9" stop-color="#221F20"></stop>
            </linearGradient>
          </defs>
        </svg>
      </div>` : '');

  // Build styleNoDate div content
  const uomQuantity = product.uom_quantity?.value || '';
  const uomQuantityNum = Number.parseInt(uomQuantity) || 0;

  let styleNoDateContent = '';
  if (styleNumber) {
    styleNoDateContent += `<p class="productstyleNumber">${styleNumber}</p>`;
  }

  if (hasOneDayDelivery) {
    styleNoDateContent += '<p class="delivarydate">1 day delivery</p>';
  }

  // Check if compare_at_price > price and has multipack_combo tag
  // Prices from GraphQL are in cents, so divide by 100 to get rupees
  const compareAtPriceNum = Number.parseFloat(variantCompareAtPrice) / 100;
  const priceNum = Number.parseFloat(variantPrice) / 100;
  if (compareAtPriceNum > priceNum && hasMultipackCombo) {
    const savingAmount = (compareAtPriceNum - priceNum).toFixed(0).replace('.00', '');
    styleNoDateContent += `<span class="saving-zone-multi-packs multipack_combo"><span class="savingAmt">Save ₹${savingAmount}</span><span> with Pack of</span> ${uomQuantity}</span>`;
  }

  // Check if uom_quantity > 1 and doesn't have multipack_combo tag
  if (uomQuantityNum > 1 && !hasMultipackCombo) {
    styleNoDateContent += `<span class="saving-zone-multi-packs"><span>Pack of</span> ${uomQuantity}</span>`;
  }

  // Build data-gtm-product-info JSON
  const gtmProductInfo = {
    section_name: 'category-page',
    position: index + 1,
    size_variant: variantTitle.replace(/'/g, '`'),
    variant_sku: variantSku,
    variant_barcode: Number(variantBarcode) || 0,
    parent_category_name: product.parent_category?.value || '',
    category_name: product.second_subcategory?.value || '',
    subcategory_name: product.child_category?.value || '',
    style_number: styleNumber,
    bulk_box_material_code: product.bulk_box_material_code?.value || '',
    sap_material_code: product.sap_material_code?.value || '',
    colour_family: product.colour_family?.value || '',
    jockey_collection: product.jockey_collection?.value || '',
    product_color_name: product.product_color_name?.value || ''
  };

  // Escape JSON for HTML attribute (replace single quotes with HTML entity)
  const gtmProductInfoJson = JSON.stringify(gtmProductInfo).replace(/'/g, '&#39;');

  return `
    <li
      data-productID="${productId}"
      data-jockey-collection="${jockeyCollection}"
      data-fabric="${fabric}"
      data-bra-padding-filter="${braPaddingFilter}"
      data-pack-multipacks-filter="${packMultipacksFilter}"
      data-closure-filter="${closureFilter}"
      data-waist-rise="${waistRise}"
      data-bra-wiring-filter="${braWiringFilter}"
      data-back-style-filter="${backStyleFilter}"
      data-straps-filter="${strapsFilter}"
      data-bra-front-coverage-filter="${braFrontCoverageFilter}"
      data-top-rear-coverage-filter="${topRearCoverageFilter}"
      data-rear-coverage-filter="${rearCoverageFilter}"
      data-filter-colour="${filterColour}"
      data-product-all-filters="#"
      data-product-type="${product.productType || ''}"
      data-styleNumber="${styleNumber}"
      data-applicableFilters="${applicableFilters}"
      data-options="${options}"
      data-maxPrice="${maxPriceFormatted}"
      class="product grid__item loop_index_${index} page_index_1"
    >
      <div class="new_card_product d${productId}" data-id="dataInJson" data-product-id="d${productId}">
        ${bestsellerTag}
        <div class="new_card_product_content">
          <div class="card_wishlist">
     
            <span
              class="smartwishlist wishlistcardButton"
              data-product="${productId}"
              data-variant="${firstVariant?.id?.replace('gid://shopify/ProductVariant/', '') || ''}"
              data-gtm-product-info='${gtmProductInfoJson}'
              onclick="wishlistEvent(this)"
            ></span>
          </div>

          <div class="new_card_product_media">
            <div class="swiper mySwiper collection-cardSwiper swiperWidth">
              <div class="swiper-wrapper">
                <div class="sld-img">
                  <a href="${productUrl}">
                    <img
                      src="${imageSrc}"
                      ${primaryImage?.srcset ? `srcset="${primaryImage.srcset}"` : ''}
                      alt="${imageAlt}"
                      width="${primaryImage?.width || 420}"
                      height="${primaryImage?.height || 513}"
                      loading="lazy"
                      class="motion-reduce media_product"
                      sizes="(max-width: 1024px) 220px, (min-width: 1024px) 360px, (min-width:1400px) 420px"
                    >
                  </a>
                </div>
              </div>
            </div>
            ${colorVariantBadge}
          </div>

          <div class="new_card_product_info">
            <div class="styleNoDate">
              ${styleNoDateContent}
            </div>
            <a href="${productUrl}">
              <h3 class="custom_card_heading">${productTitle}</h3>
            </a>

            <div class="btn_price">
              <div class="price">
                <div class="price__container" data-vendor="${product.vendor || ''}" data-proid="${productId}" data-price="${Number.parseFloat(minPrice) / 100}">
                  <div class="price__regular">
                    <span class="price-item price-item--regular product_price">${formattedMinPrice}</span>
                    <span class="pricetax">(Incl. Of All Taxes)</span>
                  </div>
                </div>
              </div>
              <div class="showColorModal">
                <button
                  class="cloneBtn"
                  onclick="ATBModalOpen(event, '${productId}', '${colorVariantsIds}', '${product.select_size_guide?.value || ''}', 'category-page'),singleVariantSelectATB()"
                  aria-label="Button for Add to Bag Popup"
                >
                  <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg" class="add-to-bag-svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M11.2988 3.93606C11.2988 1.90046 9.60755 0.231445 7.5405 0.231445C5.47345 0.231445 3.7823 1.90053 3.7823 3.93593V5.46251H0.859289C0.504293 5.46251 0.23291 5.72707 0.23291 6.07314V15.6398C0.23291 17.3293 1.63178 18.693 3.3648 18.693H8.7463C8.52841 18.3161 8.34935 17.9139 8.21458 17.4919L3.36452 17.492C2.3205 17.492 1.48538 16.6778 1.48538 15.6601V6.70401L3.7823 6.70387V7.76225C3.7823 8.10833 4.05368 8.37289 4.40867 8.37289C4.76367 8.37289 5.03505 8.10833 5.03505 7.76225V6.70387H10.0461V7.76225C10.0461 8.10833 10.3175 8.37289 10.6725 8.37289C11.0275 8.37289 11.2988 8.10833 11.2988 7.76225V6.70387H13.5956V9.48248C13.7547 9.47015 13.9155 9.46387 14.0778 9.46387C14.3388 9.46387 14.5959 9.48011 14.8483 9.51163V6.07314C14.8483 5.72707 14.5769 5.46251 14.2219 5.46251L11.2988 5.46265V3.93606ZM7.5405 1.45243C8.91853 1.45243 10.046 2.57202 10.046 3.93572V5.4623H5.03498V3.93572C5.03498 2.57188 6.16246 1.45243 7.5405 1.45243Z" fill="#fff"/>
                    <path d="M14.0773 12.5402C13.6997 12.5402 13.3936 12.8462 13.3936 13.2239V14.9334H11.6841C11.3065 14.9334 11.0004 15.2394 11.0004 15.6171C11.0004 15.9947 11.3065 16.3008 11.6841 16.3008H13.3936V18.0103C13.3936 18.3879 13.6997 18.694 14.0773 18.694C14.455 18.694 14.761 18.3879 14.761 18.0103V16.3008H16.4705C16.8482 16.3008 17.1542 15.9947 17.1542 15.6171C17.1542 15.2394 16.8482 14.9334 16.4705 14.9334H14.761V13.2239C14.761 12.8462 14.455 12.5402 14.0773 12.5402Z" fill="#fff"/>
                  </svg>
                  <span>Add to bag</span>
                  <svg class="loading-spinner" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><style>.spinner_aj0A{transform-origin:center;animation:spinner_KYSC .75s infinite linear}@keyframes spinner_KYSC{100%{transform:rotate(360deg)}}</style><path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A"></path></svg>
                </button>
              </div>
            </div>
          </div>

          <div data-in-json="d${productId}" style="display: none">
            {
              "product_handle": "${productHandle}",
              "media": ${JSON.stringify(transformMediaFormat(product.media?.edges?.map(e => e.node) || []))}
            }
          </div>
        </div>
      </div>
    </li>
  `;
}

/**
 * Creates a skeleton loader HTML element
 */
function createSkeletonLoader() {
  const skeleton = document.createElement('li');
  skeleton.className = 'grid__item grid__item--skeleton';
  skeleton.innerHTML = `
    <skeleton style="
      --skeleton-width: 100%;
      --skeleton-height: 100%;
      --skeleton-min-height: 559px;
      --skeleton-radius: 13px;
      --skeleton-mt: 6px;
      --skeleton-bg: rgba(170, 170, 170,0.2);
      --skeleton-shine-color: rgba(255, 255, 255, 0.3);
      --skeleton-animation-duration: 1.2s;"
    >
    </skeleton>
  `;
  return skeleton;
}

/**
 * Calculates how many products are remaining to load
 */
function getRemainingProductsCount() {
  // Get total product count from global variable set in Liquid
  const totalProducts = typeof all_products_count !== 'undefined' ? all_products_count : null;

  if (totalProducts === null) {
    // Fallback: if count is not available, return 8 (default batch size)
    return 8;
  }

  const remaining = totalProducts - paginationState.totalProductsLoaded;
  return Math.max(0, remaining);
}

/**
 * Adds skeleton loaders to the product grid
 */
function addSkeletonLoaders(count) {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid) {
    console.error('Product grid container not found');
    return;
  }

  // Ensure we don't add more skeletons than products remaining
  const remainingProducts = getRemainingProductsCount();
  const skeletonsToAdd = Math.min(count, remainingProducts);

  for (let i = 0; i < skeletonsToAdd; i++) {
    const skeleton = createSkeletonLoader();
    productGrid.appendChild(skeleton);
  }
}

/**
 * Appends product cards to the product grid and replaces skeleton loaders
 */
function renderProductCards(products, startIndex = 0, replaceSkeletons = true) {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid) {
    console.error('Product grid container not found');
    return;
  }

  // Replace skeleton loaders with product cards
  if (replaceSkeletons) {
    const skeletonLoaders = productGrid.querySelectorAll('.grid__item--skeleton');
    const skeletonsToReplace = Math.min(skeletonLoaders.length, products.length);

    products.slice(0, skeletonsToReplace).forEach((product, index) => {
      const skeleton = skeletonLoaders[index];
      if (skeleton) {
        const globalIndex = startIndex + index;
        const productCardHTML = generateProductCardHTML(product, globalIndex);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = productCardHTML.trim();
        const productCard = tempDiv.firstElementChild;

        skeleton.replaceWith(productCard);

        const newCardProduct = productCard.querySelector('.new_card_product');
        if (newCardProduct && window.innerWidth > 767) {
          newCardProduct.addEventListener('mouseenter', initializeProductCardSwiper);
          newCardProduct.addEventListener('mouseleave', destroyProductCardSwiper);
        }
      }
    });
  } else {
    // Append new product cards
    products.forEach((product, index) => {
      const globalIndex = startIndex + index;
      const productCardHTML = generateProductCardHTML(product, globalIndex);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = productCardHTML.trim();
      let productCard = tempDiv.firstElementChild;
      productGrid.appendChild(productCard);

      productCard = productCard.querySelector('.new_card_product');

      if (window.innerWidth > 767) {
        productCard.addEventListener('mouseenter', initializeProductCardSwiper);
        productCard.addEventListener('mouseleave', destroyProductCardSwiper);
      }
    });
  }
}

// Map to store Swiper instances by product ID
const swiperInstancesMap = new Map();

// Global state for pagination
let paginationState = {
  collectionHandle: null,
  currentCursor: null,
  hasNextPage: false,
  totalProductsLoaded: 0,
  isLoading: false,
  intersectionObserver: null
};

/**
 * Sets up Intersection Observer on the first skeleton loader
 */
function setupSkeletonObserver() {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid) return;

  // Disconnect existing observer if any
  if (paginationState.intersectionObserver) {
    paginationState.intersectionObserver.disconnect();
  }

  // Find the first skeleton loader
  const firstSkeleton = productGrid.querySelector('.grid__item--skeleton');
  if (!firstSkeleton) {
    // No more skeletons, stop observing
    return;
  }

  // Create new Intersection Observer
  paginationState.intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !paginationState.isLoading && paginationState.hasNextPage) {
          loadMoreProducts();
        }
      });
    },
    {
      rootMargin: '100px' // Start loading 100px before the skeleton is visible
    }
  );

  // Observe the first skeleton
  paginationState.intersectionObserver.observe(firstSkeleton);
}

/**
 * Loads more products using cursor-based pagination
 * Triggered by Intersection Observer when first skeleton becomes visible
 */
async function loadMoreProducts() {
  if (paginationState.isLoading || !paginationState.hasNextPage) {
    return;
  }

  paginationState.isLoading = true;

  try {
    // Calculate how many products to fetch (up to 8, or remaining count if less)
    const remainingProducts = getRemainingProductsCount();
    const fetchLimit = Math.min(8, remainingProducts);

    if (fetchLimit === 0) {
      paginationState.hasNextPage = false;
      paginationState.isLoading = false;
      return;
    }

    const result = await fetchCollectionProducts(
      paginationState.collectionHandle,
      paginationState.currentCursor,
      fetchLimit
    );

    if (result.products.length === 0) {
      paginationState.hasNextPage = false;
      paginationState.isLoading = false;
      // Remove remaining skeletons if no more products
      const productGrid = document.getElementById('product-grid');
      if (productGrid) {
        const remainingSkeletons = productGrid.querySelectorAll('.grid__item--skeleton');
        remainingSkeletons.forEach(skeleton => skeleton.remove());
      }
      return;
    }

    // Render the new products (replace skeletons)
    renderProductCards(result.products, paginationState.totalProductsLoaded, true);

    // Update pagination state
    paginationState.currentCursor = result.pageInfo.endCursor;
    paginationState.hasNextPage = result.pageInfo.hasNextPage;
    paginationState.totalProductsLoaded += result.products.length;

    // Handle skeleton loaders based on results
    const productGrid = document.getElementById('product-grid');
    if (productGrid) {
      const remainingProducts = getRemainingProductsCount();

      if (paginationState.hasNextPage && remainingProducts > 0) {
        // Add skeleton loaders for remaining products (up to 8, or remaining count if less)
        const skeletonsToAdd = Math.min(8, remainingProducts);
        addSkeletonLoaders(skeletonsToAdd);
      } else {
        // No more products remaining, remove any skeletons
        const remainingSkeletons = productGrid.querySelectorAll('.grid__item--skeleton');
        remainingSkeletons.forEach(skeleton => skeleton.remove());
        if (remainingProducts === 0) {
          paginationState.hasNextPage = false;
        }
      }
    }

    // Dispatch custom event for other scripts
    document.dispatchEvent(new CustomEvent('collectionProductsLoaded', {
      detail: {
        products: result.products,
        collectionHandle: paginationState.collectionHandle,
        hasMore: paginationState.hasNextPage
      }
    }));

    // Set up observer on the new first skeleton
    if (paginationState.hasNextPage) {
      setupSkeletonObserver();
    }

    paginationState.isLoading = false;

  } catch (error) {
    console.error('Error loading more products:', error);
    paginationState.isLoading = false;
  }
}


/**
 * Main function to initialize collection products
 * Fetches first batch to get cursor, then sets up Intersection Observer
 */
async function initCollectionProducts() {
  // Get collection handle from the script tag variable
  const pathParts = globalThis.location.pathname.split('/pages/');
  const collectionHandle = typeof collection_handle !== 'undefined' ? collection_handle : (pathParts.length > 1 ? pathParts[1].split('/')[0] : null) || 'underwear-for-men';

  if (!collectionHandle) {
    console.error('Collection handle not found');
    return;
  }

  // Initialize pagination state
  paginationState.collectionHandle = collectionHandle;
  paginationState.currentCursor = null;
  paginationState.totalProductsLoaded = 4; // First 4 products are already rendered in HTML

  try {
    // Fetch first batch (8 products) to get the cursor
    // We'll skip first 4 products (already rendered) and render products 5-8
    const result = await fetchCollectionProducts(collectionHandle, null, 8);

    if (result.products.length > 0) {
      // Skip first 4 products (already rendered) and render the rest
      const productsToRender = result.products.slice(4);

      if (productsToRender.length > 0) {
        renderProductCards(productsToRender, 4, true);
        paginationState.totalProductsLoaded += productsToRender.length;
      }

      // Update pagination state with cursor (cursor is for after the 8th product)
      // This cursor will be used for the next batch (products 9+)
      paginationState.currentCursor = result.pageInfo.endCursor;
      paginationState.hasNextPage = result.pageInfo.hasNextPage;

      // Adjust skeletons based on remaining products
      const productGrid = document.getElementById('product-grid');
      if (productGrid) {
        const remainingProducts = getRemainingProductsCount();
        const currentSkeletons = productGrid.querySelectorAll('.grid__item--skeleton');

        if (remainingProducts === 0 || !paginationState.hasNextPage) {
          // No more products remaining, remove all skeletons
          currentSkeletons.forEach(skeleton => skeleton.remove());
          paginationState.hasNextPage = false;
        } else if (currentSkeletons.length > remainingProducts) {
          // Remove excess skeletons - we have more skeletons than products remaining
          const skeletonsToRemove = currentSkeletons.length - remainingProducts;
          for (let i = 0; i < skeletonsToRemove; i++) {
            currentSkeletons[currentSkeletons.length - 1 - i].remove();
          }
        }
        // If currentSkeletons.length < remainingProducts, that's fine -
        // we'll add more when needed in loadMoreProducts
      }
    } else {
      // No products found, remove all skeletons
      const productGrid = document.getElementById('product-grid');
      if (productGrid) {
        const remainingSkeletons = productGrid.querySelectorAll('.grid__item--skeleton');
        remainingSkeletons.forEach(skeleton => skeleton.remove());
      }
      paginationState.hasNextPage = false;
    }
  } catch (error) {
    console.error('Error fetching initial products:', error);
  }

  // Set up Intersection Observer on the first skeleton loader
  setupSkeletonObserver();

  // Dispatch custom event for other scripts that might need to know initialization is complete
  document.dispatchEvent(new CustomEvent('collectionProductsInitialized', {
    detail: {
      collectionHandle,
      totalProductsLoaded: paginationState.totalProductsLoaded
    }
  }));
}

// Expose loadMoreProducts globally for button-based loading if needed
globalThis.loadMoreProducts = loadMoreProducts;

// Map to store swiper instances by product ID
const swiperInstances = new Map();

function initializeProductCardSwiper(event) {
  // Only run on desktop (width >= 767px)
  if (window.innerWidth < 767) return;

  const parent = event.currentTarget;
  if (!parent) return;

  const swiperContainer = parent.querySelector('.collection-cardSwiper');
  if (!swiperContainer) return;

  const swiperWrapper = parent.querySelector('.swiper-wrapper');
  const productID = parent.dataset.productId;
  const dataDiv = parent.querySelector('[data-in-json]');

  if (dataDiv) {
    try {
      const data = JSON.parse(dataDiv.innerText);

      // Build slides if not already built
      if (!swiperWrapper.getAttribute('slides-built')) {
        let slides = '';

        data.media.forEach((media) => {
          const imageSource = media?.src?.trim();
          const altText = media?.alt?.trim() || 'Product image';
          const productHandle = data.product_handle?.trim();

          if (imageSource && imageSource !== 'undefined' && typeof imageSource === 'string' && imageSource.trim() !== '' && media.media_type === 'image') {
            // Generate srcset - check if URL already has query params
            const separator = imageSource.includes('?') ? '&' : '?';
            const srcset = `${imageSource}${separator}width=220 220w, ${imageSource}${separator}width=360 360w, ${imageSource}${separator}width=420 420w`;
            const src = `${imageSource}${separator}width=420`;

            // Build common image HTML
            const imageHTML = `
              <img
                src="${src}"
                alt="${altText}"
                srcset="${srcset}"
                width="420"
                height="513"
                loading="lazy"
                class="motion-reduce media_product"
                sizes="(max-width: 1024px) 220px, (min-width: 1024px) 360px, (min-width:1400px) 420px"
              >
            `;

            // Determine wrapper element based on productHandle
            const wrapperTag = productHandle ? `<a href="/products/${productHandle}">` : '<div>';
            const wrapperCloseTag = productHandle ? '</a>' : '</div>';

            slides += `
              <div class="new_card_product_media swiper-slide" style="display: none;">
                ${wrapperTag}${imageHTML}${wrapperCloseTag}
              </div>
            `;
          }
        });

        swiperWrapper.innerHTML += slides;
        swiperWrapper.setAttribute('slides-built', true);
        // Don't remove dataDiv - keep it for future use
      }
    } catch (error) {
      console.error('Error parsing product data:', error);
    }
  }

  // Show slides and hide initial image
  const sldImg = swiperContainer.querySelector('.sld-img');
  const swiperSlides = swiperContainer.querySelectorAll('.swiper-slide');
  if (sldImg) sldImg.style.display = 'none';
  swiperSlides.forEach((slide) => (slide.style.display = 'block'));

  // Initialize or start swiper
  let swiper = swiperInstances.get(productID);
  if (!swiper && typeof Swiper !== 'undefined') {
    swiper = new Swiper(swiperContainer, {
      direction: 'horizontal',
      loop: true,
      autoplay: {
        delay: 1000,
        disableOnInteraction: false,
      },
    });
    // Store swiper instance in Map
    swiperInstances.set(productID, swiper);
  } else if (swiper) {
    swiper.autoplay.start();
  }
}

function destroyProductCardSwiper(event) {
  // Only run on desktop (width >= 767px)
  if (window.innerWidth < 767) return;

  const parent = event.currentTarget;
  if (!parent) return;

  const swiperContainer = parent.querySelector('.collection-cardSwiper');
  if (!swiperContainer) return;

  const productID = parent.dataset.productId;
  const swiper = swiperInstances.get(productID);

  // Show initial image and hide slides
  const sldImg = swiperContainer.querySelector('.sld-img');
  const swiperSlides = swiperContainer.querySelectorAll('.swiper-slide');
  if (sldImg) sldImg.style.display = 'block';
  swiperSlides.forEach((slide) => (slide.style.display = 'none'));

  // Stop autoplay and reset to first slide
  if (swiper) {
    swiper.autoplay.stop();
    swiper.slideTo(0, 1);
  }
}

// Initialize when DOM is ready
(async () => {
  if (document.readyState === 'loading') {
    await new Promise(resolve => {
      document.addEventListener('DOMContentLoaded', resolve);
    });
  }

  try {
    // Set up event listeners for first 4 products (already rendered in HTML)
    let firstFourProducts = document.querySelectorAll('#product-grid li .new_card_product');
    firstFourProducts.forEach((productCard, index) => {
      productCard.addEventListener('mouseenter', initializeProductCardSwiper);
      productCard.addEventListener('mouseleave', destroyProductCardSwiper);
    });

    // Initialize collection products (sets up Intersection Observer)
    await initCollectionProducts();
  } catch (error) {
    console.error('Failed to initialize collection products:', error);
  }
})();

